// Session Storage Adapter
// P1.0: Wraps existing sessionStorage logic in the adapter interface

import type { IStorageAdapter } from "../storage-adapter"
import type { Memory } from "@/lib/constants/memory"
import { STORAGE_KEY, KEY_DETAILS_STORAGE_KEY, MEMORY_CHANGED_EVENT, getFieldMetadata } from "@/lib/constants/memory"

/**
 * SessionAdapter stores memories in browser sessionStorage.
 * This is the default adapter for unauthenticated users.
 */
export class SessionAdapter implements IStorageAdapter {
  readonly name = "session"
  readonly isReadOnly = false

  private listeners = new Set<() => void>()

  private getStoredMemories(): Memory[] {
    if (typeof window === "undefined") return []
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      const memories = stored ? JSON.parse(stored) : []
      return memories.map((m: Memory, idx: number) => ({
        ...m,
        id: m.id || `memory-${idx}-${m.timestamp || Date.now()}`,
      }))
    } catch {
      return []
    }
  }

  private setStoredMemories(memories: Memory[]): void {
    if (typeof window === "undefined") return
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memories))
      this.emitChange()
    } catch (error) {
      console.error("[SessionAdapter] Failed to save memories:", error)
    }
  }

  private getStoredKeyDetailsCount(): number {
    if (typeof window === "undefined") return 0
    try {
      const stored = sessionStorage.getItem(KEY_DETAILS_STORAGE_KEY)
      return stored ? Number.parseInt(stored, 10) : 0
    } catch {
      return 0
    }
  }

  private setStoredKeyDetailsCount(count: number): void {
    if (typeof window === "undefined") return
    try {
      sessionStorage.setItem(KEY_DETAILS_STORAGE_KEY, Math.min(5, count).toString())
    } catch (error) {
      console.error("[SessionAdapter] Failed to save key details count:", error)
    }
  }

  private emitChange(): void {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(MEMORY_CHANGED_EVENT))
    }
    this.listeners.forEach((listener) => listener())
  }

  async getMemories(includeArchived = false): Promise<Memory[]> {
    const memories = this.getStoredMemories()
    return includeArchived ? memories : memories.filter((m) => !m.isArchived)
  }

  async saveMemory(memory: {
    field: string
    label: string
    value: string
    section: string
  }): Promise<Memory> {
    const existing = this.getStoredMemories()
    const filtered = existing.filter((m) => m.field !== memory.field)

    const fieldMeta = getFieldMetadata(memory.field)

    const newMemory: Memory = {
      id: `memory-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      field: memory.field,
      label: memory.label,
      value: memory.value,
      section: memory.section,
      sectionLabel: fieldMeta?.sectionLabel,
      category: fieldMeta?.category,
      categoryLabel: fieldMeta?.categoryLabel,
      timestamp: Date.now(),
      isArchived: false,
    }

    filtered.push(newMemory)
    this.setStoredMemories(filtered)

    // Update key details count
    if (fieldMeta) {
      const currentCount = this.getStoredKeyDetailsCount()
      const uniqueFields = new Set(filtered.filter((m) => !m.isArchived).map((m) => m.field))
      const coreFieldIds = ["business_name", "industry", "products_services", "target_market", "goals"]
      const completedCoreFields = coreFieldIds.filter((f) => uniqueFields.has(f)).length
      if (completedCoreFields > currentCount) {
        this.setStoredKeyDetailsCount(completedCoreFields)
      }
    }

    return newMemory
  }

  async updateMemory(id: string, updates: Partial<Memory>): Promise<void> {
    const memories = this.getStoredMemories()
    const updated = memories.map((m) => (m.id === id ? { ...m, ...updates, timestamp: Date.now() } : m))
    this.setStoredMemories(updated)
  }

  async deleteMemory(id: string): Promise<void> {
    const memories = this.getStoredMemories()
    const filtered = memories.filter((m) => m.id !== id)
    this.setStoredMemories(filtered)
  }

  async archiveMemory(id: string): Promise<void> {
    await this.updateMemory(id, { isArchived: true })
  }

  async restoreMemory(id: string): Promise<void> {
    await this.updateMemory(id, { isArchived: false })
  }

  async getMemoryByField(fieldId: string): Promise<Memory | undefined> {
    const memories = this.getStoredMemories()
    return memories.find((m) => m.field === fieldId && !m.isArchived)
  }

  async getKeyDetailsCompleted(): Promise<number> {
    return this.getStoredKeyDetailsCount()
  }

  async clearAll(): Promise<void> {
    if (typeof window === "undefined") return
    sessionStorage.removeItem(STORAGE_KEY)
    sessionStorage.removeItem(KEY_DETAILS_STORAGE_KEY)
    this.emitChange()
  }

  subscribe(callback: () => void): () => void {
    this.listeners.add(callback)

    const handleStorageChange = () => callback()

    if (typeof window !== "undefined") {
      window.addEventListener(MEMORY_CHANGED_EVENT, handleStorageChange)
      window.addEventListener("storage", handleStorageChange)
    }

    return () => {
      this.listeners.delete(callback)
      if (typeof window !== "undefined") {
        window.removeEventListener(MEMORY_CHANGED_EVENT, handleStorageChange)
        window.removeEventListener("storage", handleStorageChange)
      }
    }
  }
}

// Singleton instance
let sessionAdapter: SessionAdapter | null = null

export function getSessionAdapter(): SessionAdapter {
  if (!sessionAdapter) {
    sessionAdapter = new SessionAdapter()
  }
  return sessionAdapter
}
